import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const steps = [
  { icon: "ri-shopping-basket-line", key: "step1", color: "from-amber-500 to-amber-600" },
  { icon: "ri-gift-2-line", key: "step2", color: "from-amber-400 to-orange-500" },
  { icon: "ri-edit-2-line", key: "step3", color: "from-rose-500 to-rose-600" },
  { icon: "ri-price-tag-3-line", key: "step4", color: "from-emerald-400 to-green-500" },
];

export default function CustomBoxSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 md:py-28 bg-gradient-to-br from-amber-50 via-[#f5ede0] to-rose-50 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10"
          style={{ background: "radial-gradient(circle, #f43f5e, transparent)", transform: "translate(20%, -20%)" }} />
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full opacity-10"
          style={{ background: "radial-gradient(circle, #f59e0b, transparent)", transform: "translate(-20%, 20%)" }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Content */}
          <div
            className="transition-all duration-800"
            style={{ opacity: visible ? 1 : 0, transform: visible ? "translateX(0)" : "translateX(-50px)", transitionDuration: "0.8s" }}
          >
            <span className="inline-block text-amber-700 text-xs font-semibold uppercase tracking-widest mb-3">
              — Personalized For You —
            </span>
            <h2
              className="text-4xl md:text-5xl font-bold text-stone-800 mb-4 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("custom_title")}
            </h2>
            <p className="text-gray-600 text-lg mb-10">{t("custom_subtitle")}</p>

            <div className="grid grid-cols-2 gap-4 mb-10">
              {steps.map((step, i) => (
                <div
                  key={step.key}
                  className="flex items-start gap-3 p-4 bg-white rounded-2xl border border-rose-100 hover:border-rose-200 hover:-translate-y-1 transition-all duration-300"
                  style={{
                    opacity: visible ? 1 : 0,
                    transform: visible ? "translateY(0)" : "translateY(20px)",
                    transition: `all 0.5s ease`,
                    transitionDelay: `${0.3 + i * 0.1}s`,
                  }}
                >
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${step.color} flex-shrink-0`}>
                    <i className={`${step.icon} text-white text-base`}></i>
                  </div>
                  <div>
                    <span className="text-xs text-amber-600 font-semibold">Step {i + 1}</span>
                    <p className="text-sm font-semibold text-gray-700 mt-0.5">{t(`custom_${step.key}`)}</p>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/custom-gift-box"
              className="group inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-600 to-rose-600 text-white font-bold rounded-full hover:shadow-2xl hover:shadow-amber-500/30 hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-gift-2-line text-lg"></i>
              {t("custom_cta")}
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-200"></i>
            </Link>
          </div>

          {/* Right: Image */}
          <div
            className="relative transition-all duration-800"
            style={{ opacity: visible ? 1 : 0, transform: visible ? "translateX(0)" : "translateX(50px)", transitionDuration: "0.8s", transitionDelay: "0.2s" }}
          >
            <div className="relative rounded-3xl overflow-hidden h-[480px]">
              <img
                src="https://readdy.ai/api/search-image?query=beautiful%20custom%20gift%20box%20being%20assembled%20with%20flowers%20candles%20books%20and%20gourmet%20items%20on%20elegant%20cream%20table%2C%20luxury%20boutique%20gift%20creation%20process%2C%20warm%20golden%20light%2C%20premium%20presentation%2C%20romantic%20and%20beautiful&width=700&height=800&seq=custom1&orientation=portrait"
                alt="Custom Gift Box"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-rose-900/40 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 border border-rose-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-amber-500 to-rose-600">
                      <i className="ri-gift-line text-white text-base"></i>
                    </div>
                    <div>
                      <p className="font-bold text-gray-800 text-sm">Custom Gift Box</p>
                      <p className="text-xs text-gray-500">Starting from 15,000 XAF</p>
                    </div>
                    <div className="ml-auto">
                      <span className="px-2.5 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">Popular</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating badge */}
            <div
              className="absolute -top-4 -right-4 bg-gradient-to-br from-amber-400 to-orange-500 text-white rounded-2xl p-4 shadow-xl"
              style={{ animation: "floatBadge 4s ease-in-out infinite" }}
            >
              <p className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>100%</p>
              <p className="text-xs font-medium opacity-90">Personalized</p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes floatBadge {
          0%, 100% { transform: translateY(0px) rotate(-3deg); }
          50% { transform: translateY(-10px) rotate(3deg); }
        }
      `}</style>
    </section>
  );
}